import { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Pagination, Form, InputGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useGetProductsQuery } from '../../slices/productsApiSlice';
import { addToCart } from '../../slices/cartSlice';
import Rating from '../Rating';
import Loader from '../Loader';
import Message from '../Message';
import SectionTitle from './SectionTitle';
import { formatNTD } from '../../utils/pricing';

// Catalog section for the one-page store: browse + instant search + add to cart,
// all without leaving the page. Search is server-side (reuses getProducts'
// keyword param, the same one the old HomeScreen search used) and debounced
// 400ms, so we fire one request after typing stops — not one per keystroke.
const CatalogSection = () => {
  const dispatch = useDispatch();
  const [term, setTerm] = useState('');        // what the user is typing
  const [keyword, setKeyword] = useState('');  // debounced value sent to the API
  const [pageNumber, setPageNumber] = useState(1);

  // Debounce: 400ms after the last keystroke, commit the term and reset to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setKeyword(term.trim());
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isLoading, isFetching, error } = useGetProductsQuery({
    keyword,
    pageNumber,
  });

  const addToCartHandler = (product) => {
    dispatch(addToCart({ ...product, qty: 1 }));
    toast.success(`已將「${product.name}」加入購物車`);
  };

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-between mb-3"
        style={{ gap: '12px' }}
      >
        <SectionTitle step={1} className="mb-0">選購商品</SectionTitle>
        <InputGroup style={{ maxWidth: '340px' }}>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="搜尋商品…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-label="搜尋商品"
          />
          {term && (
            <Button
              variant="outline-secondary"
              onClick={() => setTerm('')}
              aria-label="清除搜尋"
            >
              <FaTimes />
            </Button>
          )}
        </InputGroup>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          {keyword && (
            <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
              {isFetching
                ? '搜尋中…'
                : `「${keyword}」共找到 ${data.products.length} 項商品`}
            </p>
          )}

          {data.products.length === 0 ? (
            <Message>找不到符合「{keyword}」的商品。</Message>
          ) : (
            <Row>
              {data.products.map((product) => (
                <Col key={product._id} xs={12} sm={6} lg={4} xl={3} className="my-2">
                  {/* my-2 lives on the Col (not the Card) so h-100 + margin can't overflow the row */}
                  <Card className="p-3 rounded h-100 op-card">
                    <Link to={`/product/${product._id}`}>
                      <Card.Img
                        src={product.image}
                        alt={product.name}
                        variant="top"
                        className="op-card-img"
                      />
                    </Link>
                    <Card.Body className="d-flex flex-column">
                      <Link to={`/product/${product._id}`}>
                        <Card.Title as="div" className="product-title">
                          <strong>{product.name}</strong>
                        </Card.Title>
                      </Link>
                      <Card.Text as="div">
                        <Rating
                          value={product.rating}
                          text={`${product.numReviews} 則評論`}
                        />
                      </Card.Text>
                      <Card.Text as="div" className="op-price">
                        {formatNTD(product.price)}
                      </Card.Text>
                      {product.countInStock === 0 ? (
                        <Badge className="mt-auto op-oos">已售完</Badge>
                      ) : (
                        <Button
                          variant="success"
                          className="mt-auto w-100 op-add-btn"
                          onClick={() => addToCartHandler(product)}
                        >
                          加入購物車
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {data.pages > 1 && (
            <Pagination>
              {[...Array(data.pages).keys()].map((x) => (
                <Pagination.Item
                  key={x + 1}
                  active={x + 1 === pageNumber}
                  onClick={() => setPageNumber(x + 1)}
                >
                  {x + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          )}
        </>
      )}
    </>
  );
};

export default CatalogSection;
